import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useLoadScript, GoogleMap, Marker, StreetViewPanorama } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, DEFAULT_LOCATION } from '../constants/placeTypes';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TopBar from './TopBar';
import StreetWalk from './StreetWalk';
import API_BASE_URL from '../config/api';
import axios from 'axios';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const libraries = ['places', 'geometry'];

// Helper to get datetime-local format string (YYYY-MM-DDTHH:mm)
const getDateTimeLocalString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Get 24 hours ago as default filter
const getDefault24HoursAgo = () => {
  const now = new Date();
  now.setHours(now.getHours() - 24);
  return getDateTimeLocalString(now);
};

const mapContainerStyle = {
  width: '100%',
  height: '300px'
};

const streetViewContainerStyle = {
  width: '100%',
  height: '400px'
};

// Street-scene (prospect/refuge) indicators. Data shape comes from
// StreetSceneAnalyzer in the backend: {method, indices, features, segmentation}.
// Inspired by Akcelik et al. 2024 (J. Environ. Psychol. 97:102344): "refuge"
// (enclosed, low sky) was the only proxy that predicted preference.
const SCENE_INDEX_ROWS = [
  { key: 'refuge', label: 'Skydd (refuge)', hint: 'Omslutet, lite himmel – den riktning som förutsade preferens i studien' },
  { key: 'enclosure', label: 'Omslutenhet', hint: 'Andel byggnader, murar och staket' },
  { key: 'openness', label: 'Öppenhet (prospect)', hint: 'Himmel, väg och ljus – öppen vy' },
  { key: 'naturalness', label: 'Naturlighet', hint: 'Grönska, mark, organiska kanter och färgmättnad' },
];

const SCENE_COMPOSITION_GROUPS = [
  { key: 'sky', label: 'Himmel', classes: ['sky'], color: '#BCD7EA' },
  { key: 'built', label: 'Byggt', classes: ['building', 'wall', 'fence'], color: 'var(--clay)' },
  { key: 'vegetation', label: 'Grönska', classes: ['vegetation', 'terrain'], color: 'var(--green)' },
  { key: 'ground', label: 'Mark', classes: ['road', 'sidewalk'], color: 'var(--line)' },
  { key: 'vehicles', label: 'Fordon', classes: ['car', 'truck', 'bus', 'train', 'motorcycle', 'bicycle'], color: 'var(--ink-soft)' },
  { key: 'people', label: 'Människor', classes: ['person', 'rider'], color: 'var(--ink)' },
  { key: 'other', label: 'Övrigt', classes: ['pole', 'traffic_light', 'traffic_sign'], color: 'var(--paper-clay)' },
];

const SceneAnalysisPanel = ({ scene, compact = false }) => {
  if (!scene || !scene.indices) return null;
  const seg = scene.segmentation;
  const groups = seg
    ? SCENE_COMPOSITION_GROUPS
        .map(g => ({ ...g, value: g.classes.reduce((sum, c) => sum + (seg[c] || 0), 0) }))
        .filter(g => g.value >= 0.005)
    : null;

  return (
    <div className={`scene-analysis${compact ? ' compact' : ''}`}>
      {!compact && <h4 data-tip="street.scene">Gatumiljö (prospect / refuge)</h4>}
      <div className="scene-index-list">
        {SCENE_INDEX_ROWS.map(r => (
          <div className="scene-index-row" key={r.key} data-tip={compact ? r.hint : undefined}>
            <span className="scene-index-label">{r.label}</span>
            <span className="scene-index-bar">
              <span className="scene-index-fill" style={{ width: `${scene.indices[r.key] ?? 0}%` }} />
            </span>
            <span className="scene-index-value">{scene.indices[r.key] ?? '–'}</span>
            {!compact && <span className="scene-index-hint">{r.hint}</span>}
          </div>
        ))}
      </div>
      {groups && (
        <div className="scene-composition">
          <div className="scene-composition-bar">
            {groups.map(g => (
              <span key={g.key} style={{ width: `${g.value * 100}%`, background: g.color }} data-tip={`${g.label}: ${Math.round(g.value * 100)}% av bilden`} />
            ))}
          </div>
          {!compact && (
            <div className="scene-composition-legend">
              {groups.map(g => (
                <span key={g.key}><i style={{ background: g.color }} />{g.label} {Math.round(g.value * 100)}%</span>
              ))}
            </div>
          )}
        </div>
      )}
      {!compact && (
        <p className="scene-analysis-note">
          {seg
            ? 'Bildens innehåll klassat med SegFormer (Cityscapes). '
            : 'Utan segmenteringsmodell – himmel och grönska uppskattade från färg. '}
          Indikatorerna är riktningsvärden (0–100) enligt Akcelik m.fl. 2024, inte en kalibrerad preferenspoäng.
        </p>
      )}
    </div>
  );
};

const StreetAnalyzer = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const { user } = useAuth();

  // State
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [streetViewImage, setStreetViewImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [heading, setHeading] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(''); // Progress message for async
  const [isFetchingStreetView, setIsFetchingStreetView] = useState(false);
  const [status, setStatus] = useState('Klicka på kartan för att välja en plats, eller ladda upp en bild.');
  // Two tools on one page: 'interest' (Visuellt intresse) and 'walk' (Promenadfilm, ?tool=walk)
  const [searchParams, setSearchParams] = useSearchParams();
  const tool = searchParams.get('tool') === 'walk' ? 'walk' : 'interest';
  const selectTool = (t) => setSearchParams(t === 'walk' ? { tool: 'walk' } : {}, { replace: true });
  const [walkOpened, setWalkOpened] = useState(false);
  if (tool === 'walk' && !walkOpened) setWalkOpened(true);
  const [inputMode, setInputMode] = useState('streetview'); // Visuellt intresse: 'streetview', 'upload' or 'history'
  const isImageMode = tool === 'interest' && (inputMode === 'streetview' || inputMode === 'upload');
  const [mapCenter] = useState(DEFAULT_LOCATION);
  const [historicalAnalyses, setHistoricalAnalyses] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showBiophilicDetails, setShowBiophilicDetails] = useState(false);
  const [showEmptyBlockDetails, setShowEmptyBlockDetails] = useState(false);
  const [extractBuilding, setExtractBuilding] = useState(true); // Default to enabled
  const [extractedImage, setExtractedImage] = useState(null);

  // Extraction preview state (two-step flow)
  const [extractionPreview, setExtractionPreview] = useState(null); // {extractedImage, maskImage, buildingCoverage}
  const [isExtracting, setIsExtracting] = useState(false);
  const [originalImageForAnalysis, setOriginalImageForAnalysis] = useState(null); // Store original for re-extraction
  const [uploadedFileName, setUploadedFileName] = useState(null); // Track uploaded file name
  const [filterStartDate, setFilterStartDate] = useState(getDefault24HoursAgo); // Filter for history display and export
  const [includeAllUsers, setIncludeAllUsers] = useState(false); // Checkbox: include all users' analyses
  const [currentPage, setCurrentPage] = useState(1); // Pagination state
  const ITEMS_PER_PAGE = 20; // Max items per page

  const fileInputRef = useRef(null);
  const streetViewRef = useRef(null);
  const imgRef = useRef(null);

  // Cropping state
  const [crop, setCrop] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);

  // Handle map click
  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setSelectedLocation({ lat, lng });
    setStreetViewImage(null);
    setAnalysisResult(null);
    setCrop(null);
    setCroppedImage(null);
    setStatus(`Plats vald: ${lat.toFixed(5)}, ${lng.toFixed(5)}. Klicka "Hämta Street View" för att ladda bilden.`);
  }, []);

  // Fetch Street View image via backend API
  const fetchStreetView = async () => {
    if (!selectedLocation) {
      setStatus('Välj först en plats på kartan.');
      return;
    }

    setIsFetchingStreetView(true);
    setStatus('Hämtar Street View bild...');

    try {
      const response = await axios.post(`${API_BASE_URL}/streetview`, {
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        heading: heading,
        pitch: pitch,
        fov: 90,
        size: '640x640'
      });

      if (response.data.image) {
        setStreetViewImage(response.data.image);
        setUploadedImage(null);
        setUploadedFileName(null); // Clear filename for Street View images
        setCrop(null);
        setCroppedImage(null);
        setStatus('Street View bild hämtad. Beskär bilden eller klicka "Analysera byggnad" för att få poäng.');
      } else {
        setStatus('Kunde inte hämta Street View bild för denna plats.');
      }
    } catch (error) {
      console.error('Error fetching Street View:', error);
      setStatus(`Fel vid hämtning av Street View: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsFetchingStreetView(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setStatus('Vänligen välj en bildfil (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setStatus('Bilden är för stor. Max 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
      setUploadedFileName(file.name); // Store the filename
      setStreetViewImage(null);
      setAnalysisResult(null);
      setCrop(null);
      setCroppedImage(null);
      setStatus('Bild uppladdad. Beskär bilden eller klicka "Analysera byggnad" för att få poäng.');
    };
    reader.readAsDataURL(file);
  };

  // Poll for analysis status
  const pollAnalysisStatus = async (jobId, maxAttempts = 60, interval = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await axios.get(`${API_BASE_URL}/analyze/status/${jobId}`, {
          withCredentials: true
        });

        const { status: jobStatus } = response.data;

        if (jobStatus === 'completed') {
          return response.data;
        } else if (jobStatus === 'failed') {
          throw new Error(response.data.error || 'Analysen misslyckades');
        } else if (jobStatus === 'processing') {
          setAnalysisProgress('Bearbetar bild...');
        } else {
          setAnalysisProgress('Väntar på analys...');
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (error) {
        if (error.response?.status === 404) {
          // Job not found yet, keep waiting
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
        throw error;
      }
    }
    throw new Error('Tidsgräns för analys överskreds');
  };

  // Extract building preview (first step of two-step flow)
  const extractBuildingPreview = async () => {
    const imageToExtract = croppedImage || streetViewImage || uploadedImage;

    if (!imageToExtract) {
      setStatus('Ingen bild att extrahera. Hämta Street View eller ladda upp en bild först.');
      return;
    }

    setIsExtracting(true);
    setExtractionPreview(null);
    setOriginalImageForAnalysis(imageToExtract); // Store for later analysis
    setStatus('Extraherar byggnad...');

    try {
      const response = await axios.post(`${API_BASE_URL}/analyze/extract`, {
        image: imageToExtract
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        setExtractionPreview({
          extractedImage: response.data.extractedImage,
          maskImage: response.data.maskImage,
          buildingCoverage: response.data.buildingCoverage
        });
        setStatus(`Byggnad extraherad (${response.data.buildingCoverage.toFixed(1)}% täckning). Granska resultatet och godkänn eller välj en annan bild.`);
      } else {
        setStatus(`Extrahering misslyckades: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error extracting building:', error);
      setStatus(`Fel vid extrahering: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsExtracting(false);
    }
  };

  // Approve extraction and run full analysis (second step of two-step flow)
  const approveExtractionAndAnalyze = async () => {
    if (!extractionPreview || !originalImageForAnalysis) {
      setStatus('Ingen extraherad bild att analysera.');
      return;
    }

    setIsAnalyzing(true);
    setExtractedImage(null);
    setAnalysisProgress('Startar analys med extraherad byggnad...');
    setStatus('Analyserar extraherad byggnad...');

    try {
      // Always use extractionPreview.extractedImage for analysis
      const imageForAnalysis = extractionPreview.extractedImage;

      // Build request payload
      const payload = {
        image: imageForAnalysis,
        extractBuilding: false, // Already extracted
        // Send all images so they can be stored in S3 for history
        originalImage: originalImageForAnalysis,
        extractedImage: extractionPreview.extractedImage, // The final extracted image (used for analysis)
        maskImage: extractionPreview.maskImage,
        fileName: uploadedFileName || 'Street View' // Track source filename
      };


      // Add location data if available
      if (selectedLocation) {
        payload.location = {
          lat: selectedLocation.lat,
          lng: selectedLocation.lng,
          heading: heading,
          pitch: pitch
        };
      }

      // Start the async analysis job
      const startResponse = await axios.post(`${API_BASE_URL}/analyze/start`, payload, {
        withCredentials: true
      });

      const { jobId } = startResponse.data;
      setAnalysisProgress('Analys startad, bearbetar...');

      // Poll for results
      const result = await pollAnalysisStatus(jobId);

      // Add extraction info to result
      result.buildingExtracted = true;
      result.buildingCoverage = extractionPreview.buildingCoverage;

      setAnalysisResult(result);
      setExtractedImage(extractionPreview.extractedImage);
      setExtractionPreview(null); // Clear preview
      setStatus('Analys klar!');
      setAnalysisProgress('');
    } catch (error) {
      console.error('Error analyzing image:', error);
      setStatus(`Fel vid analys: ${error.response?.data?.error || error.message}`);
      setAnalysisProgress('');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Cancel extraction preview and go back to image selection
  const cancelExtractionPreview = () => {
    setExtractionPreview(null);
    setOriginalImageForAnalysis(null);
    setStatus('Extrahering avbruten. Välj en annan bild eller justera beskärningen.');
  };

  // Analyze the image (using async flow) - direct analysis without preview
  const analyzeImage = async () => {
    const imageToAnalyze = croppedImage || streetViewImage || uploadedImage;

    if (!imageToAnalyze) {
      setStatus('Ingen bild att analysera. Hämta Street View eller ladda upp en bild först.');
      return;
    }

    setIsAnalyzing(true);
    setExtractedImage(null); // Reset extracted image
    setExtractionPreview(null); // Clear any preview
    setAnalysisProgress('Startar analys...');
    setStatus(extractBuilding ? 'Extraherar byggnad och analyserar...' : 'Analyserar byggnadsfasad...');

    try {
      // Build request payload with image and optional location
      const payload = {
        image: imageToAnalyze,
        extractBuilding: extractBuilding,
        fileName: uploadedFileName || 'Street View'
      };


      // Add location data if available (from Street View mode)
      if (selectedLocation) {
        payload.location = {
          lat: selectedLocation.lat,
          lng: selectedLocation.lng,
          heading: heading,
          pitch: pitch
        };
      }

      // Start the async analysis job
      const startResponse = await axios.post(`${API_BASE_URL}/analyze/start`, payload, {
        withCredentials: true
      });

      const { jobId } = startResponse.data;
      setAnalysisProgress('Analys startad, bearbetar...');

      // Poll for results
      const result = await pollAnalysisStatus(jobId);

      setAnalysisResult(result);

      // Store extracted image URL if building extraction was performed
      if (result.buildingExtracted && (result.extractedImage || result.extractedImageUrl)) {
        setExtractedImage(result.extractedImage || result.extractedImageUrl);
      }

      setStatus('Analys klar!');
      setAnalysisProgress('');
    } catch (error) {
      console.error('Error analyzing image:', error);
      setStatus(`Fel vid analys: ${error.response?.data?.error || error.message}`);
      setAnalysisProgress('');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Analyze the image directly without any extraction - just the cropped or original image
  const analyzeDirectly = async () => {
    const imageToAnalyze = croppedImage || streetViewImage || uploadedImage;

    if (!imageToAnalyze) {
      setStatus('Ingen bild att analysera. Hämta Street View eller ladda upp en bild först.');
      return;
    }

    setIsAnalyzing(true);
    setExtractedImage(null);
    setExtractionPreview(null);
    setAnalysisProgress('Startar analys...');
    setStatus('Analyserar byggnadsfasad...');

    try {
      const payload = {
        image: imageToAnalyze,
        extractBuilding: false,
        // Street-scene (prospect/refuge) analysis runs on whatever is sent — cropped or whole image
        analyzeScene: true,
        fileName: uploadedFileName || 'Street View'
      };


      // Add location data if available
      if (selectedLocation) {
        payload.location = {
          lat: selectedLocation.lat,
          lng: selectedLocation.lng,
          heading: heading,
          pitch: pitch
        };
      }

      const startResponse = await axios.post(`${API_BASE_URL}/analyze/start`, payload, {
        withCredentials: true
      });

      const { jobId } = startResponse.data;
      setAnalysisProgress('Analys startad, bearbetar...');

      const result = await pollAnalysisStatus(jobId);

      setAnalysisResult(result);
      setStatus('Analys klar!');
      setAnalysisProgress('');
    } catch (error) {
      console.error('Error analyzing image:', error);
      setStatus(`Fel vid analys: ${error.response?.data?.error || error.message}`);
      setAnalysisProgress('');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Get score color based on value
  const getScoreColor = (score) => {
    if (score >= 70) return '#22c55e'; // green
    if (score >= 50) return '#eab308'; // yellow
    if (score >= 30) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  // Get fractal dimension color class based on optimal range (1.3-1.5)
  const getFractalClass = (d) => {
    if (!d) return '';
    if (d >= 1.3 && d <= 1.5) return 'optimal';
    if (d >= 1.2 && d <= 1.6) return 'good';
    return 'suboptimal';
  };

  // Handle Street View POV (heading/pitch) change
  const handleStreetViewPovChange = () => {
    if (streetViewRef.current) {
      const pov = streetViewRef.current.getPov();
      if (pov) {
        setHeading(Math.round(pov.heading || 0));
        setPitch(Math.round(pov.pitch || 0));
      }
    }
  };

  // Handle Street View position change (when user walks/moves)
  const handleStreetViewPositionChange = () => {
    if (streetViewRef.current) {
      const pos = streetViewRef.current.getPosition();
      if (pos) {
        const newLat = pos.lat();
        const newLng = pos.lng();
        // Only update if position actually changed
        if (!selectedLocation ||
            Math.abs(selectedLocation.lat - newLat) > 0.00001 ||
            Math.abs(selectedLocation.lng - newLng) > 0.00001) {
          setSelectedLocation({ lat: newLat, lng: newLng });
          // Clear the fetched image since position changed
          setStreetViewImage(null);
          setAnalysisResult(null);
          setCrop(null);
          setCroppedImage(null);
          setStatus(`Position ändrad: ${newLat.toFixed(5)}, ${newLng.toFixed(5)}. Klicka "Hämta Street View" för ny bild.`);
        }
      }
    }
  };

  // Handle image load for cropping - set default crop to fit image
  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    // Set initial crop to cover the entire image
    setCrop({
      unit: 'px',
      x: 0,
      y: 0,
      width: width,
      height: height
    });
  }, []);

  // Generate cropped image from crop selection
  const applyCrop = useCallback(() => {
    if (!imgRef.current || !crop || !crop.width || !crop.height) {
      setStatus('Markera ett område att beskära först.');
      return;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCroppedImage(croppedDataUrl);
    setStatus('Bild beskuren. Klicka "Analysera byggnad" för att analysera det beskurna området.');
  }, [crop]);

  // Reset crop and use original image
  const resetCrop = useCallback(() => {
    setCrop(null);
    setCroppedImage(null);
    setStatus('Beskärning återställd. Hela bilden kommer analyseras.');
  }, []);

  // Fetch historical analyses with optional date filter
  const fetchHistoricalAnalyses = useCallback(async (fromDate) => {
    console.log('[fetchHistoricalAnalyses] Called with fromDate:', fromDate);
    setIsLoadingHistory(true);
    try {
      // Build URL with query params
      let url = `${API_BASE_URL}/analyses`;
      if (fromDate && fromDate.trim() !== '') {
        // Convert datetime-local format to ISO string for API
        const dateObj = new Date(fromDate);
        if (!isNaN(dateObj.getTime())) {
          const isoDate = dateObj.toISOString();
          url += `?fromDate=${encodeURIComponent(isoDate)}`;
          console.log('[fetchHistoricalAnalyses] Using ISO date:', isoDate);
        } else {
          console.log('[fetchHistoricalAnalyses] Invalid date, skipping filter');
        }
      } else {
        console.log('[fetchHistoricalAnalyses] No date filter applied');
      }

      console.log('[fetchHistoricalAnalyses] Fetching URL:', url);
      const response = await axios.get(url, {
        withCredentials: true
      });
      console.log('[fetchHistoricalAnalyses] Got', response.data.analyses?.length || 0, 'analyses');
      setHistoricalAnalyses(response.data.analyses || []);
    } catch (error) {
      console.error('Error fetching analyses:', error);
      setStatus(`Fel vid hämtning av analyser: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [API_BASE_URL]);

  // Load history when tab is selected
  useEffect(() => {
    if (inputMode === 'history') {
      fetchHistoricalAnalyses(filterStartDate);
    }
  }, [inputMode, fetchHistoricalAnalyses]);

  // Filter analyses by owner (mine vs all)
  const filteredAnalyses = useMemo(() => {
    if (includeAllUsers) {
      return historicalAnalyses;
    }
    // Filter to only show current user's analyses
    const currentUserId = user?.userId;
    if (!currentUserId) {
      return historicalAnalyses; // If no user ID, show all
    }
    return historicalAnalyses.filter(analysis => analysis.userId === currentUserId);
  }, [historicalAnalyses, includeAllUsers, user?.userId]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAnalyses.length / ITEMS_PER_PAGE);
  const paginatedAnalyses = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredAnalyses.slice(startIndex, endIndex);
  }, [filteredAnalyses, currentPage, ITEMS_PER_PAGE]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [includeAllUsers, historicalAnalyses]);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('sv-SE');
  };

  // Resize image to thumbnail for Excel export
  // Handles both base64 data URLs and external URLs (via fetch)
  const resizeImageToThumbnail = async (imageData, maxWidth = 200) => {
    try {
      let imageSrc = imageData;

      // If it's an external URL (not base64), fetch it first to avoid CORS issues
      if (imageData.startsWith('http')) {
        const response = await fetch(imageData, {
          mode: 'cors',
          cache: 'no-cache'  // Bypass cache to get fresh CORS headers
        });
        if (!response.ok) {
          console.error('Failed to fetch image:', response.status);
          return null;
        }
        const blob = await response.blob();
        imageSrc = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const ratio = maxWidth / img.width;
          const canvas = document.createElement('canvas');
          canvas.width = maxWidth;
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // Return base64 without data URL prefix
          const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
          resolve({ base64, width: canvas.width, height: canvas.height });
        };
        img.onerror = () => resolve(null);
        img.src = imageSrc;
      });
    } catch (err) {
      console.error('Error resizing image:', err);
      return null;
    }
  };

  // Export analyses to Excel with images and filename
  // Format: rows = metrics, columns = different analysis runs
  // Note: historicalAnalyses is already filtered by the API based on filterStartDate
  const exportToExcel = async () => {
    if (filteredAnalyses.length === 0) {
      setStatus('Inga analyser att exportera.');
      return;
    }

    setStatus(`Genererar Excel-fil med ${filteredAnalyses.length} analyser...`);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Gatuanalyser');

    // Collect all unique feature keys across analyses
    const allFeatureKeys = new Set();
    filteredAnalyses.forEach(analysis => {
      if (analysis.features) {
        Object.keys(analysis.features).forEach(key => allFeatureKeys.add(key));
      }
    });

    // Define the rows (metrics) we want to export
    const metricRows = [
      // Basic info
      { key: 'thumbnail', label: 'Bild' },
      { key: 'fileName', label: 'Filnamn' },
      { key: 'timestamp', label: 'Tidstämpel' },
      { key: 'userId', label: 'Användare' },
      // Scores
      { key: 'heatmapScore', label: 'AI-poäng' },
      { key: 'score', label: 'CV-poäng' },
      // Selected features (fixed display order)
      { key: 'fractalComplexity', label: 'Fraktal komplexitet (högre=mer komplex)' },
      { key: 'feature_detail_complexity', label: 'Detail Richness' },
      { key: 'feature_edge_density_medium', label: 'Medium Details' },
      { key: 'feature_edge_density_fine', label: 'Fine Details' },
      { key: 'feature_texture_contrast', label: 'Surface Roughness' },
      { key: 'feature_edge_density_coarse', label: 'Major Edges' },
      // Average (calculated)
      { key: 'average', label: 'Genomsnitt' },
      // Street-scene indicators
      { key: 'scene_refuge', label: 'Skydd (refuge)' },
      { key: 'scene_enclosure', label: 'Omslutenhet' },
      { key: 'scene_openness', label: 'Öppenhet (prospect)' },
      { key: 'scene_naturalness', label: 'Naturlighet' },
      { key: 'sceneFeature_sky_fraction', label: 'Andel himmel' },
      { key: 'sceneFeature_built_fraction', label: 'Andel byggt' },
      { key: 'sceneFeature_vegetation_fraction', label: 'Andel grönska' },
      { key: 'sceneFeature_non_straight_edge_share', label: 'Andel icke-raka kanter' },
    ];

    // Sort analyses by timestamp (oldest first for columns left-to-right)
    const sortedAnalyses = [...filteredAnalyses].sort((a, b) =>
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    // Header row: Metric name + analysis timestamps
    const headerRow = ['Egenskap'];
    sortedAnalyses.forEach((analysis, idx) => {
      const dateStr = formatTimestamp(analysis.timestamp) || `Analys ${idx + 1}`;
      headerRow.push(dateStr);
    });
    worksheet.addRow(headerRow);
    worksheet.getRow(1).font = { bold: true };

    // Data rows: one row per metric
    for (const metric of metricRows) {
      const row = [metric.label];

      if (metric.key === 'thumbnail') {
        // Add placeholder for image row, images added later
        sortedAnalyses.forEach(() => row.push(''));
      } else {
        sortedAnalyses.forEach(analysis => {
          let value = '';

          if (metric.key === 'fileName') {
            value = analysis.fileName || 'Street View';
          } else if (metric.key === 'timestamp') {
            value = formatTimestamp(analysis.timestamp);
          } else if (metric.key === 'userId') {
            value = analysis.userId || '';
          } else if (metric.key.startsWith('feature_')) {
            const featureKey = metric.key.replace('feature_', '');
            value = analysis.features?.[featureKey] ?? '';
          } else if (metric.key.startsWith('scene_')) {
            value = analysis.sceneAnalysis?.indices?.[metric.key.replace('scene_', '')] ?? '';
          } else if (metric.key.startsWith('sceneFeature_')) {
            value = analysis.sceneAnalysis?.features?.[metric.key.replace('sceneFeature_', '')] ?? '';
          } else if (metric.key === 'average') {
            // Calculate average of all feature percentages
            const featureKeys = ['texture_contrast', 'detail_complexity', 'edge_density_fine', 'edge_density_medium', 'edge_density_coarse'];
            const featureValues = featureKeys.map(k => analysis.features?.[k]).filter(v => typeof v === 'number');
            const fractalVal = analysis.fractalComplexity;
            const allValues = fractalVal !== undefined ? [...featureValues, fractalVal] : featureValues;
            if (allValues.length > 0) {
              const avg = allValues.reduce((a, b) => a + b, 0) / allValues.length;
              value = Math.round(avg * 100) + '%';
            } else {
              value = '';
            }
          } else {
            value = analysis[metric.key] ?? '';
          }

          // Format numbers - features and fractalComplexity as percentages
          if (typeof value === 'number') {
            if (metric.key.startsWith('feature_') || metric.key.startsWith('sceneFeature_') || metric.key === 'fractalComplexity') {
              value = Math.round(value * 100) + '%';
            } else {
              value = Number(value.toFixed(4));
            }
          }

          row.push(value);
        });
      }
      worksheet.addRow(row);
    }

    // Add images to row 2 (thumbnail row)
    const imageRowIndex = 2;
    worksheet.getRow(imageRowIndex).height = 150; // Set row height for images

    for (let colIdx = 0; colIdx < sortedAnalyses.length; colIdx++) {
      const analysis = sortedAnalyses[colIdx];
      const imageData = analysis.imageData;

      if (imageData) {
        try {
          const thumbnail = await resizeImageToThumbnail(imageData, 200);
          if (thumbnail) {
            const imageId = workbook.addImage({
              base64: thumbnail.base64,
              extension: 'jpeg',
            });

            worksheet.addImage(imageId, {
              tl: { col: colIdx + 1, row: imageRowIndex - 1 },
              ext: { width: thumbnail.width, height: thumbnail.height }
            });
          }
        } catch (err) {
          console.error('Error adding image:', err);
        }
      }
    }

    // Set column widths
    worksheet.getColumn(1).width = 25;
    for (let i = 2; i <= sortedAnalyses.length + 1; i++) {
      worksheet.getColumn(i).width = 30;
    }

    // Generate filename with current date
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `gatuanalyser_${dateStr}.xlsx`;

    // Generate and download file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, filename);

    setStatus(`Exporterade ${sortedAnalyses.length} analyser till ${filename}`);
  };

  if (loadError) {
    return <div className="error">Fel vid laddning av kartor: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div className="status">Laddar karta...</div>;
  }

  return (
    <>
      <TopBar />
      <div className="street-analyzer ut-page">
      <div className="ut-page-head">
        <div className="ut-eyebrow">Gatuanalys</div>
        {/* Tool tabs: two separate tools on one page, shown as the page title */}
        <div className="street-tool-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tool === 'interest'}
            className={tool === 'interest' ? 'active' : ''}
            onClick={() => selectTool('interest')}
            data-tip="street.tool.interest"
          >
            Visuellt intresse
          </button>
          <button
            role="tab"
            aria-selected={tool === 'walk'}
            className={tool === 'walk' ? 'active' : ''}
            onClick={() => selectTool('walk')}
            data-tip="street.tool.walk"
          >
            Promenadfilm
          </button>
        </div>
        <p className="ut-lede">
          {tool === 'walk'
            ? 'Se promenaden mellan två platser som en film av Street View-bilder.'
            : 'Mät hur mycket en fasad drar blicken – från Street View eller en egen bild.'}
        </p>
      </div>

      {/* Walk film - stays mounted once opened so a generated walk survives tool switches */}
      {walkOpened && (
        <div hidden={tool !== 'walk'}>
          <StreetWalk active={tool === 'walk'} />
        </div>
      )}

      {/* Input Mode Selector */}
      {tool === 'interest' && (
        <div className="input-mode-selector">
          <button
            className={inputMode === 'streetview' ? 'active' : ''}
            onClick={() => setInputMode('streetview')}
            data-tip="street.mode.streetview"
          >
            Google Street View
          </button>
          <button
            className={inputMode === 'upload' ? 'active' : ''}
            onClick={() => setInputMode('upload')}
            data-tip="street.mode.upload"
          >
            Ladda upp bild
          </button>
          <button
            className={inputMode === 'history' ? 'active' : ''}
            onClick={() => setInputMode('history')}
            data-tip="street.mode.history"
          >
            Analyser
          </button>
        </div>
      )}

      {/* Street View Mode */}
      {tool === 'interest' && inputMode === 'streetview' && (
        <div className="streetview-section">
          <div className="map-section">
            <h3>1. Välj plats på kartan</h3>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={15}
              onClick={handleMapClick}
              options={{
                streetViewControl: false,
                mapTypeControl: false
              }}
            >
              {selectedLocation && (
                <Marker position={selectedLocation} />
              )}
            </GoogleMap>
          </div>

          {selectedLocation && (
            <div className="streetview-controls">
              <h3>2. Navigera i Street View och hämta bild</h3>

              {/* Interactive Street View Preview */}
              <div className="streetview-preview">
                <GoogleMap
                  mapContainerStyle={streetViewContainerStyle}
                  center={selectedLocation}
                  zoom={14}
                >
                  <StreetViewPanorama
                    position={selectedLocation}
                    visible={true}
                    onLoad={(panorama) => {
                      streetViewRef.current = panorama;
                    }}
                    onPovChanged={handleStreetViewPovChange}
                    onPositionChanged={handleStreetViewPositionChange}
                    options={{
                      pov: { heading, pitch },
                      enableCloseButton: false,
                      addressControl: false,
                      fullscreenControl: false
                    }}
                  />
                </GoogleMap>
              </div>

              <button
                onClick={fetchStreetView}
                disabled={isFetchingStreetView}
                className="fetch-btn"
                data-tip="street.fetch"
              >
                {isFetchingStreetView ? 'Hämtar...' : 'Hämta bild'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Upload Mode */}
      {tool === 'interest' && inputMode === 'upload' && (
        <div className="upload-section">
          <h3>Ladda upp en byggnadsbild</h3>
          <p className="upload-hint">Välj en bild på en byggnadsfasad för analys (max 10MB)</p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            className="upload-btn"
            data-tip="street.choose"
          >
            Välj bild...
          </button>
        </div>
      )}

      {/* History Mode */}
      {tool === 'interest' && inputMode === 'history' && (
        <div className="history-section">
          <div className="history-header">
            <h3>Tidigare analyser</h3>
            <div className="history-count">
              {!isLoadingHistory && filteredAnalyses.length > 0 && (
                <span>
                  Visar {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredAnalyses.length)} av {filteredAnalyses.length}
                  {historicalAnalyses.length !== filteredAnalyses.length && ` (${historicalAnalyses.length} totalt)`}
                </span>
              )}
            </div>
            <div className="filter-controls">
              <label className="owner-filter-checkbox" data-tip="street.allUsers">
                <input
                  type="checkbox"
                  checked={includeAllUsers}
                  onChange={(e) => setIncludeAllUsers(e.target.checked)}
                />
                <span>Ta med allas</span>
              </label>
              <label className="filter-date-label" data-tip="street.from">
                Från:
                <input
                  type="datetime-local"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="filter-date-input"
                />
              </label>
              <button
                onClick={() => {
                  console.log('[Sök button] Clicked, filterStartDate:', filterStartDate);
                  fetchHistoricalAnalyses(filterStartDate);
                }}
                className="apply-filter-btn"
                data-tip="street.search"
              >
                Sök
              </button>
              <button
                onClick={() => {
                  setFilterStartDate('');
                  fetchHistoricalAnalyses('');
                }}
                className="clear-filter-btn"
                data-tip="street.showAll"
              >
                Visa alla
              </button>
              {filteredAnalyses.length > 0 && (
                <button
                  onClick={exportToExcel}
                  className="export-btn"
                  data-tip="street.export"
                >
                  Exportera till Excel ({filteredAnalyses.length})
                </button>
              )}
            </div>
          </div>
          {isLoadingHistory ? (
            <div className="loading-history">Laddar analyser...</div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="no-analyses">
              Inga analyser hittades för valt datumintervall.
              {historicalAnalyses.length > 0 && !includeAllUsers && (
                <span> ({historicalAnalyses.length} totalt, men inga från dig)</span>
              )}
            </div>
          ) : (
            <div className="analyses-list">
              {paginatedAnalyses.map((analysis) => (
                <div key={analysis.analysisId} className="analysis-card">
                  <div className="analysis-card-images">
                    {/* Show original image */}
                    <div className="analysis-card-image">
                      <div className="image-label">Original</div>
                      {analysis.imageData ? (
                        <img src={analysis.imageData} alt="Originalbild" />
                      ) : (
                        <div className="no-image">Ingen bild</div>
                      )}
                    </div>
                    {/* Show extracted image if available */}
                    {analysis.extractedImageUrl && (
                      <div className="analysis-card-image">
                        <div className="image-label">Extraherad</div>
                        <img src={analysis.extractedImageUrl} alt="Extraherad byggnad" />
                      </div>
                    )}
                    <div className="analysis-card-scores">
                      <div className="analysis-card-score" style={{ backgroundColor: getScoreColor(analysis.score) }}>
                        <span className="score-type">CV</span>
                        <span className="score-num">{analysis.score}</span>
                      </div>
                      {analysis.heatmapScore !== undefined && (
                        <div className="analysis-card-score" style={{ backgroundColor: getScoreColor(analysis.heatmapScore) }}>
                          <span className="score-type">AI</span>
                          <span className="score-num">{analysis.heatmapScore}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="analysis-card-details">
                    <div className="analysis-card-header">
                      <span className="analysis-timestamp">{formatTimestamp(analysis.timestamp)}</span>
                      {analysis.fileName && (
                        <span className="analysis-filename">{analysis.fileName}</span>
                      )}
                    </div>
                    <div className="analysis-scores-summary">
                      <span className="score-badge" style={{ backgroundColor: getScoreColor(analysis.score) }}>
                        CV: {analysis.score}
                      </span>
                      {analysis.heatmapScore !== undefined && (
                        <span className="score-badge" style={{ backgroundColor: getScoreColor(analysis.heatmapScore) }}>
                          AI: {analysis.heatmapScore}
                        </span>
                      )}
                    </div>
                    {/* Interpretation - TEMPORARILY HIDDEN
                    <div className="analysis-interpretation">{analysis.interpretation}</div>
                    */}
                    <SceneAnalysisPanel scene={analysis.sceneAnalysis} compact />
                    {/* Feature table in history - showing selected features */}
                    {analysis.features && (
                      <table className="feature-scores-table compact">
                        <tbody>
                          {(() => {
                            const orderedRows = [
                              { key: 'fractalComplexity', label: 'Fraktal komplexitet', value: analysis.fractalComplexity },
                              { key: 'detail_complexity', label: analysis.featureLabels?.detail_complexity || 'Detail Richness', value: analysis.features?.detail_complexity },
                              { key: 'edge_density_medium', label: analysis.featureLabels?.edge_density_medium || 'Medium Details', value: analysis.features?.edge_density_medium },
                              { key: 'edge_density_fine', label: analysis.featureLabels?.edge_density_fine || 'Fine Details', value: analysis.features?.edge_density_fine },
                              { key: 'texture_contrast', label: analysis.featureLabels?.texture_contrast || 'Surface Roughness', value: analysis.features?.texture_contrast },
                              { key: 'edge_density_coarse', label: analysis.featureLabels?.edge_density_coarse || 'Major Edges', value: analysis.features?.edge_density_coarse },
                            ].filter(r => typeof r.value === 'number');
                            const average = orderedRows.length > 0
                              ? Math.round(orderedRows.reduce((sum, r) => sum + Math.round(r.value * 100), 0) / orderedRows.length)
                              : 0;

                            return (
                              <>
                                {orderedRows.map(r => (
                                  <tr key={r.key} data-tip={`street.f.${r.key}`}>
                                    <td>{r.label}</td>
                                    <td className="feature-value"><strong>{Math.round(r.value * 100)}%</strong></td>
                                  </tr>
                                ))}
                                <tr className="average-row" data-tip="street.f.average">
                                  <td><strong>Genomsnitt</strong></td>
                                  <td className="feature-value"><strong>{average}%</strong></td>
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    )}
                    {/* Size-aware analysis in history - TEMPORARILY HIDDEN
                    {analysis.sizeAwareAnalysis && (
                      <div className="history-size-aware">
                        <div className="history-size-aware-header">Storleksmedveten analys</div>
                        <div className="history-size-aware-row">
                          <span>Fasadmått:</span>
                          <span>{analysis.sizeAwareAnalysis.facadeWidthM}m × {analysis.sizeAwareAnalysis.facadeHeightM}m = {analysis.sizeAwareAnalysis.facadeAreaM2} m²</span>
                        </div>
                        <div className="history-size-aware-row">
                          <span>Poäng före/efter justering:</span>
                          <span>{analysis.sizeAwareAnalysis.originalScore} → {analysis.sizeAwareAnalysis.sizeAdjustedScore}</span>
                        </div>
                        <div className="history-size-aware-row">
                          <span>Element/m²:</span>
                          <span>{analysis.sizeAwareAnalysis.elementsPerM2?.toFixed(2)}</span>
                        </div>
                        <div className="history-size-aware-interpretation">
                          {analysis.sizeAwareAnalysis.sizeComplexityRating}
                        </div>
                      </div>
                    )}
                    */}
                    {/* Biophilic metrics in history - TEMPORARILY HIDDEN
                    {analysis.fractalDimension !== undefined && (
                      <div className="history-biophilic">
                        <div className="history-biophilic-header">Biofil analys</div>
                        <div className="history-biophilic-row">
                          <span>Fraktal dimension:</span>
                          <span className={`metric-value-small ${getFractalClass(analysis.fractalDimension)}`}>
                            {analysis.fractalDimension?.toFixed(2)}
                          </span>
                        </div>
                        <div className="history-biophilic-interpretation">
                          {analysis.fractalInterpretation}
                        </div>
                        <div className="history-biophilic-row">
                          <span>Entropi:</span>
                          <span>{analysis.entropyGlobal?.toFixed(2)} bits</span>
                        </div>
                      </div>
                    )}
                    */}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {filteredAnalyses.length > ITEMS_PER_PAGE && (
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
                data-tip="street.prev"
              >
                ← Föregående
              </button>
              <span className="pagination-info">
                Sida {currentPage} av {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                data-tip="street.next"
              >
                Nästa →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Image Cropping Section - image modes only */}
      {isImageMode && (streetViewImage || uploadedImage) && !croppedImage && (
        <div className="image-crop-section">
          <h3>3. Beskär bilden (valfritt)</h3>
          <p className="crop-hint">Dra för att markera den del av bilden du vill analysera, eller analysera hela bilden direkt.</p>

          <div className="crop-container">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              className="image-cropper"
            >
              <img
                src={streetViewImage || uploadedImage}
                alt="Byggnadsbild för beskärning"
                onLoad={onImageLoad}
                style={{ maxWidth: '100%', maxHeight: '500px' }}
              />
            </ReactCrop>
          </div>


          <div className="crop-buttons">
            <button
              onClick={applyCrop}
              disabled={!crop || !crop.width || !crop.height}
              className="crop-btn"
              data-tip="street.crop"
            >
              Beskär bild
            </button>
            <button
              onClick={analyzeDirectly}
              disabled={isAnalyzing}
              className="analyze-btn"
              data-tip="street.analyze"
            >
              {isAnalyzing ? 'Analyserar...' : 'Analysera'}
            </button>
          </div>
        </div>
      )}

      {/* Cropped Image Preview - image modes only */}
      {isImageMode && croppedImage && (
        <div className="image-preview-section">
          <h3>3. Beskuren bild - redo att analysera</h3>
          <div className="image-preview">
            <img
              src={croppedImage}
              alt="Beskuren byggnadsbild"
            />
          </div>


          <div className="crop-buttons">
            <button
              onClick={resetCrop}
              className="reset-btn"
              data-tip="street.resetCrop"
            >
              Återställ beskärning
            </button>
            <button
              onClick={analyzeDirectly}
              disabled={isAnalyzing}
              className="analyze-btn"
              data-tip="street.analyze"
            >
              {isAnalyzing ? 'Analyserar...' : 'Analysera'}
            </button>
          </div>
        </div>
      )}

      {/* Analysis Result - image modes only */}
      {isImageMode && analysisResult && (
        <div className="analysis-result">
          <h3>Analysresultat</h3>

          {/* Show extracted building image if available */}
          {extractedImage && (
            <div className="extracted-building-section">
              <h4>Extraherad byggnad</h4>
              <p className="extraction-info">
                Byggnadstäckning: {analysisResult.buildingCoverage?.toFixed(1)}% av bilden
              </p>
              <div className="extracted-image-container">
                <img
                  src={extractedImage}
                  alt="Extraherad byggnad"
                  className="extracted-building-image"
                />
              </div>
            </div>
          )}

          <div className="score-display dual-scores">
            <div className="score-column" data-tip="street.scoreCV">
              <div className="score-label-header">Bildanalys (CV)</div>
              <div
                className="score-circle"
                style={{ borderColor: getScoreColor(analysisResult.score) }}
              >
                <span
                  className="score-value"
                  style={{ color: getScoreColor(analysisResult.score) }}
                >
                  {analysisResult.score}
                </span>
                <span className="score-label">/ 100</span>
              </div>
            </div>
            {analysisResult.heatmapScore !== undefined && (
              <div className="score-column" data-tip="street.scoreAI">
                <div className="score-label-header">Uppmärksamhet (AI)</div>
                <div
                  className="score-circle"
                  style={{ borderColor: getScoreColor(analysisResult.heatmapScore) }}
                >
                  <span
                    className="score-value"
                    style={{ color: getScoreColor(analysisResult.heatmapScore) }}
                  >
                    {analysisResult.heatmapScore}
                  </span>
                  <span className="score-label">/ 100</span>
                </div>
              </div>
            )}
          </div>

          {/* Interpretation - TEMPORARILY HIDDEN
          <div className="score-interpretation">
            <p>{analysisResult.interpretation}</p>
          </div>
          */}

          {/* Size-Aware Analysis Section - TEMPORARILY HIDDEN
          {analysisResult.sizeAwareAnalysis && (
            <div className="size-aware-result-section">
              <h4>Storleksmedveten analys</h4>
              <div className="size-aware-grid">
                <div className="size-aware-item">
                  <span className="size-aware-label">Fasadmått</span>
                  <span className="size-aware-value">
                    {analysisResult.sizeAwareAnalysis.facadeWidthM}m × {analysisResult.sizeAwareAnalysis.facadeHeightM}m
                  </span>
                </div>
                <div className="size-aware-item">
                  <span className="size-aware-label">Fasadyta</span>
                  <span className="size-aware-value">{analysisResult.sizeAwareAnalysis.facadeAreaM2} m²</span>
                </div>
                <div className="size-aware-item">
                  <span className="size-aware-label">Våningar (uppsk.)</span>
                  <span className="size-aware-value">{analysisResult.sizeAwareAnalysis.estimatedFloors}</span>
                </div>
                <div className="size-aware-item">
                  <span className="size-aware-label">Element per m²</span>
                  <span className="size-aware-value">{analysisResult.sizeAwareAnalysis.elementsPerM2?.toFixed(2)}</span>
                </div>
                <div className="size-aware-item">
                  <span className="size-aware-label">Poäng före justering</span>
                  <span className="size-aware-value">{analysisResult.sizeAwareAnalysis.originalScore}</span>
                </div>
                <div className="size-aware-item">
                  <span className="size-aware-label">Poäng efter justering</span>
                  <span className="size-aware-value highlight">{analysisResult.sizeAwareAnalysis.sizeAdjustedScore}</span>
                </div>
              </div>
              <div className="size-complexity-rating">
                {analysisResult.sizeAwareAnalysis.sizeComplexityRating}
              </div>
            </div>
          )}
          */}

          <div className="score-details">
            {/* CV model details - TEMPORARILY HIDDEN
            <p><strong>CV-modell:</strong> {analysisResult.model}</p>
            <p><strong>CV rått värde:</strong> {analysisResult.rawScore?.toFixed(4)}</p>
            */}
            {analysisResult.heatmapScore !== undefined && (
              <>
                <p><strong>AI-modell:</strong> {analysisResult.heatmapModel}</p>
                <p><strong>AI rått värde:</strong> {analysisResult.heatmapRawScore?.toFixed(4)}</p>
              </>
            )}
          </div>

          <SceneAnalysisPanel scene={analysisResult.sceneAnalysis} />

          {/* Feature Scores Table - showing only selected features */}
          {analysisResult.features && (
            <div className="feature-scores-section">
              <h4>Detaljerade poäng</h4>
              <table className="feature-scores-table">
                <thead>
                  <tr>
                    <th>Egenskap</th>
                    <th>Värde</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const orderedRows = [
                      { key: 'fractalComplexity', label: 'Fraktal komplexitet (högre=mer komplex)', value: analysisResult.fractalComplexity },
                      { key: 'detail_complexity', label: analysisResult.featureLabels?.detail_complexity || 'Detail Richness', value: analysisResult.features?.detail_complexity },
                      { key: 'edge_density_medium', label: analysisResult.featureLabels?.edge_density_medium || 'Medium Details', value: analysisResult.features?.edge_density_medium },
                      { key: 'edge_density_fine', label: analysisResult.featureLabels?.edge_density_fine || 'Fine Details', value: analysisResult.features?.edge_density_fine },
                      { key: 'texture_contrast', label: analysisResult.featureLabels?.texture_contrast || 'Surface Roughness', value: analysisResult.features?.texture_contrast },
                      { key: 'edge_density_coarse', label: analysisResult.featureLabels?.edge_density_coarse || 'Major Edges', value: analysisResult.features?.edge_density_coarse },
                    ].filter(r => typeof r.value === 'number');
                    const average = orderedRows.length > 0
                      ? Math.round(orderedRows.reduce((sum, r) => sum + Math.round(r.value * 100), 0) / orderedRows.length)
                      : 0;

                    return (
                      <>
                        {orderedRows.map(r => (
                          <tr key={r.key} data-tip={`street.f.${r.key}`}>
                            <td>{r.label}</td>
                            <td className="feature-value"><strong>{Math.round(r.value * 100)}%</strong></td>
                          </tr>
                        ))}
                        <tr className="average-row" data-tip="street.f.average">
                          <td><strong>Genomsnitt</strong></td>
                          <td className="feature-value"><strong>{average}%</strong></td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {/* Biophilic/Fractal Analysis Section - TEMPORARILY HIDDEN
          {analysisResult.fractalDimension !== undefined && (
            <div className="biophilic-section">
              <button
                className="biophilic-toggle"
                onClick={() => setShowBiophilicDetails(!showBiophilicDetails)}
              >
                {showBiophilicDetails ? '▼' : '▶'} Biofil analys
              </button>

              {showBiophilicDetails && (
                <div className="biophilic-details">
                  <div className="fractal-metric">
                    <span className="metric-label">Fraktal dimension:</span>
                    <span className={`metric-value ${getFractalClass(analysisResult.fractalDimension)}`}>
                      {analysisResult.fractalDimension?.toFixed(2) || 'N/A'}
                    </span>
                    <span className="optimal-range">(optimalt: 1.3-1.5)</span>
                  </div>

                  <div className="fractal-interpretation">
                    {analysisResult.fractalInterpretation}
                  </div>

                  <div className="entropy-metrics">
                    <div className="metric-row">
                      <span>Global entropi:</span>
                      <span>{analysisResult.entropyGlobal?.toFixed(2) || 'N/A'} bits</span>
                    </div>
                    <div className="metric-row">
                      <span>Lokal entropi-variation:</span>
                      <span>{analysisResult.entropyLocalStd?.toFixed(2) || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="biophilic-info">
                    <small>
                      Baserat på forskning om fraktal fluens: fasader med fraktal dimension
                      1.3-1.5 korrelerar med stressreduktion och visuell komfort.
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}
          */}

          {/* Empty Block Analysis Section - TEMPORARILY HIDDEN
          {analysisResult.features?.largest_empty_block_pixels !== undefined && (
            <div className="empty-block-section">
              <button
                className="empty-block-toggle"
                onClick={() => setShowEmptyBlockDetails(!showEmptyBlockDetails)}
              >
                {showEmptyBlockDetails ? '▼' : '▶'} Tomyta-analys
              </button>

              {showEmptyBlockDetails && (
                <div className="empty-block-details">
                  <div className="empty-block-metric highlight">
                    <span className="metric-label">Största tomyta:</span>
                    <span className="metric-value-large">
                      {analysisResult.features.largest_empty_block_percent_image?.toFixed(1)}%
                    </span>
                    <span className="metric-sublabel">av bilden</span>
                  </div>

                  <div className="empty-block-metric">
                    <span className="metric-label">Av byggnaden:</span>
                    <span className="metric-value">
                      {analysisResult.features.largest_empty_block_percent_building?.toFixed(1)}%
                    </span>
                  </div>

                  <div className="empty-block-sizes">
                    <div className="size-row">
                      <span>Största tomyta:</span>
                      <span>{analysisResult.features.largest_empty_block_pixels?.toLocaleString()} px</span>
                    </div>
                    <div className="size-row">
                      <span>Byggnadens storlek:</span>
                      <span>{analysisResult.features.building_pixels?.toLocaleString()} px</span>
                    </div>
                    <div className="size-row">
                      <span>Total bildstorlek:</span>
                      <span>{analysisResult.features.total_pixels?.toLocaleString()} px</span>
                    </div>
                    <div className="size-row">
                      <span>Antal tomytor:</span>
                      <span>{analysisResult.features.empty_block_count}</span>
                    </div>
                  </div>

                  <div className="empty-block-info">
                    <small>
                      Tomytor är områden utan detaljer (vita/transparenta).
                      Stora tomytor kan indikera monotona fasadpartier.
                    </small>
                  </div>
                </div>
              )}
            </div>
          )}
          */}
        </div>
      )}

      {/* Status - image modes only (the walk tab has its own) */}
      {isImageMode && (
        <div id="status" className={status.includes('Fel') ? 'error' : 'success'}>
          {status}
        </div>
      )}

      {/* Loading Overlay */}
      {tool === 'interest' && (isAnalyzing || isFetchingStreetView || isExtracting) && (
        <div id="loading-popup">
          <div className="spinner"></div>
          <p>{isExtracting ? 'Extraherar byggnad...' : isAnalyzing ? (analysisProgress || 'Analyserar byggnad...') : 'Hämtar Street View...'}</p>
        </div>
      )}
      </div>
    </>
  );
};

export default StreetAnalyzer;
